import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Column } from '@elementary/common-ui/components';
import * as atoms from '@elementary/common-ui/atoms';
import {
  AgentCodeTestConfigChange,
  AgentCodeTestConfigPlan,
  AgentCodeTestCreateChange,
  AgentTestOperation,
  FieldChange,
  getAgentTestChangeRowId,
  isAgentCodeTestCreateChange,
} from '@elementary/common-ui/types';
import { useMutation } from '@tanstack/react-query';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import {
  deriveParamsFromYaml,
  normalizeCodeTestParams,
} from '@elementary/common-ui/utils/codeTestConfig';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import { buildCodePlanWizardState } from 'testManagement/utils/codePlanWizardMapping';
import * as api from 'testManagement/services/api';
import { AgentConfigPreviewShell } from '../agentConfigPreviewShared';
import { BREADCRUMB_LABELS, SEVERITY_OPTIONS, STORAGE_KEY } from './constants';
import { CodePreviewRow, CodeTestConfigFieldKey } from './types';

type PreviewCodeTestPlanYamlVars = {
  envId: string;
  operation: AgentTestOperation;
  changes: AgentCodeTestConfigChange[];
  toolCallId: string;
};

const extractNew = <T,>(field?: FieldChange<T>): T | undefined => field?.new;

// Config args (where/limit/store_failures) can arrive either as explicit
// `params` or embedded in the `yaml` edit; merge both so the Parameters column
// reflects a YAML-only change too (explicit params win on conflict).
const getEffectiveParams = (
  change: AgentCodeTestConfigChange,
): Record<string, FieldChange<unknown>> => ({
  ...deriveParamsFromYaml(change.yaml),
  ...(normalizeCodeTestParams(change.params) ?? {}),
});

const buildCodeCreatePreviewRow = (
  change: AgentCodeTestCreateChange,
): CodePreviewRow => ({
  id: getAgentTestChangeRowId(change),
  asset_id: change.assetId,
  schema: '',
  table: change.resourceName,
  column: change.column,
  package: change.packageName === 'elementary' ? 'elementary' : 'dbt',
  name: change.testType,
  type: change.testType,
  severity: extractNew(change.severity) || 'warn',
  owners: extractNew(change.owners) ?? [],
  tags: extractNew(change.tags) ?? [],
  args: {},
  updated_at: '',
  meta: {},
  is_singular: false,
  is_custom_sql: false,
  original_path: '',
  is_cloud: false,
  channels: [],
  model_name: change.resourceName,
  source_name: change.sourceName,
  configChange: change,
  isCreateRow: true,
});

const getPresentFields = (
  plan: AgentCodeTestConfigPlan,
): Set<CodeTestConfigFieldKey> => {
  const fields = new Set<CodeTestConfigFieldKey>();
  if (plan.operation === 'delete') return fields;

  for (const change of plan.changes) {
    if (change.severity) fields.add('severity');
    if (change.tags) fields.add('tags');
    if (change.owners) fields.add('owners');
    if (change.description) fields.add('description');
    if (Object.keys(getEffectiveParams(change)).length > 0) {
      fields.add('params');
    }
  }
  return fields;
};

const changeNeedsYaml = (change: AgentCodeTestConfigChange): boolean =>
  !change.yaml?.new?.trim();

const CodeTestAgentConfigPreview = () => {
  const navigate = useNavigateWithEnv();
  const envId = useSelectedEnv();
  const { openSnackbar } = useSnackbar();
  const [plan, setPlan] = useRecoilState(atoms.agentCodeTestConfigPlan);
  const enrichingToolCallIdRef = useRef<string | null>(null);
  const [yamlEnrichmentFailed, setYamlEnrichmentFailed] = useState(false);

  const toolCallId = plan?.toolCallId;
  const operation = plan?.operation;
  const needsYamlEnrichment =
    !!plan &&
    operation !== 'delete' &&
    plan.changes.some((change) => changeNeedsYaml(change));

  const { mutate: enrichYaml, isLoading: isEnrichingYaml } = useMutation(
    ({
      envId: mutationEnvId,
      operation: mutationOperation,
      changes,
    }: PreviewCodeTestPlanYamlVars) =>
      api.previewCodeTestPlanYaml(mutationEnvId, mutationOperation, changes),
    {
      onSuccess: (response, { toolCallId: requestToolCallId }) => {
        if (!Array.isArray(response.changes) || response.changes.length === 0) {
          setYamlEnrichmentFailed(true);
          openSnackbar({
            message: 'Failed to load YAML for plan tests.',
            variant: 'error',
          });
          return;
        }
        setPlan((prev) => {
          if (!prev || prev.toolCallId !== requestToolCallId) return prev;
          return { ...prev, changes: response.changes };
        });
      },
      onError: () => {
        setYamlEnrichmentFailed(true);
        openSnackbar({
          message: 'Failed to load YAML for plan tests.',
          variant: 'error',
        });
      },
      onSettled: (_data, _error, { toolCallId: requestToolCallId }) => {
        if (enrichingToolCallIdRef.current === requestToolCallId) {
          enrichingToolCallIdRef.current = null;
        }
      },
    },
  );

  const isYamlReady =
    !isEnrichingYaml && (!needsYamlEnrichment || yamlEnrichmentFailed);

  useEffect(() => {
    if (!plan?.toolCallId || !envId || !needsYamlEnrichment || !operation) {
      return;
    }
    if (enrichingToolCallIdRef.current === plan.toolCallId) return;

    const requestToolCallId = plan.toolCallId;
    enrichingToolCallIdRef.current = requestToolCallId;
    setYamlEnrichmentFailed(false);
    enrichYaml({
      envId,
      operation,
      changes: plan.changes,
      toolCallId: requestToolCallId,
    });
    // for each plan, it will be executed once (not on every change in the plan)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId, toolCallId, operation, needsYamlEnrichment, enrichYaml]);

  const handleRowClick = useCallback(
    (row: CodePreviewRow) => {
      if (!plan || plan.operation === 'delete' || !isYamlReady) {
        return;
      }

      const change =
        plan.changes.find((item) => getAgentTestChangeRowId(item) === row.id) ??
        row.configChange;
      const assetId = row.asset_id || change.assetId;
      if (!assetId) return;

      navigate('/test-management/plan-edit-test', {
        state: buildCodePlanWizardState({
          change,
          operation: plan.operation as 'create' | 'update',
          assetId,
        }),
      });
    },
    [isYamlReady, navigate, plan],
  );

  const buildDynamicColumns = useCallback(
    ({
      presentFields,
      columnBuilders,
    }: Parameters<
      React.ComponentProps<
        typeof AgentConfigPreviewShell<
          AgentCodeTestConfigChange,
          CodePreviewRow
        >
      >['buildDynamicColumns']
    >[0]) => {
      const columns: Column<CodePreviewRow>[] = [];

      if (presentFields.has('params')) {
        columns.push(
          columnBuilders.buildParamsColumn(
            'code_params_change',
            'Parameters',
            (row) => getEffectiveParams(row.configChange),
          ),
        );
      }
      if (presentFields.has('severity')) {
        columns.push(
          columnBuilders.buildDropdownColumn(
            'code_severity_change',
            'Severity',
            SEVERITY_OPTIONS,
            (row) => row.configChange.severity,
          ),
        );
      }
      if (presentFields.has('description')) {
        columns.push(
          columnBuilders.buildTextColumn(
            'code_description_change',
            'Description',
            (row) => row.configChange.description,
          ),
        );
      }
      if (presentFields.has('tags')) {
        columns.push(
          columnBuilders.buildListColumn(
            'code_tags_change',
            'Tags',
            ['tag', 'tags'],
            (row) => row.configChange.tags,
            { isElasticLayout: true },
          ),
        );
      }
      if (presentFields.has('owners')) {
        columns.push(
          columnBuilders.buildListColumn(
            'code_owners_change',
            'Owners',
            ['owner', 'owners'],
            (row) => row.configChange.owners,
            { isElasticLayout: true },
          ),
        );
      }
      return columns;
    },
    [],
  );

  return (
    <AgentConfigPreviewShell
      planAtom={atoms.agentCodeTestConfigPlan}
      isCreateChange={isAgentCodeTestCreateChange}
      buildCreatePreviewRow={(change) => {
        if (!isAgentCodeTestCreateChange(change)) {
          throw new Error('Expected create change');
        }
        return buildCodeCreatePreviewRow(change);
      }}
      isCreateRowDataLoading={false}
      getPresentFields={getPresentFields}
      buildDynamicColumns={buildDynamicColumns}
      getSubmitLabel={() => 'Open PR'}
      breadcrumbLabels={BREADCRUMB_LABELS}
      storageKey={STORAGE_KEY}
      onRowClick={isYamlReady ? handleRowClick : undefined}
      draftPr={plan?.draftPr}
    />
  );
};

export default CodeTestAgentConfigPreview;
